import { create, type StoreApi } from 'zustand';
import {
  createModelsSlice,
  initialModelsState,
  type ModelsSlice,
} from '@/lib/stores/chat-store/models-slice';
import {
  createDraftsSlice,
  initialDraftsState,
  type DraftsSlice,
} from '@/lib/stores/chat-store/drafts-slice';
import {
  createOpenChatSlice,
  initialOpenChatState,
  type OpenChatSlice,
} from '@/lib/stores/chat-store/open-chat-slice';
import {
  createSessionListSlice,
  initialSessionListState,
  type SessionListSlice,
} from '@/lib/stores/chat-store/session-list-slice';
import { forgetDraftThreads } from '@/lib/stores/chat-store/drafts';
import { startNewWorkspace } from '@/lib/stores/chat-store/workspace-generation';

export type { AgentDraftSource } from '@/lib/stores/chat-store/drafts-slice';

// The chat store is made of slices (KAN-1266), each in ``chat-store/``: the
// open chat (``open-chat-slice.ts``, opening it in ``load-chat.ts``, the
// workspace it is read in in ``tenant-access.ts``), the session lists
// (``session-list-slice.ts``), models (``models-slice.ts``) and drafts
// (``drafts-slice.ts``). This file only puts them together.
interface ChatResetSlice {
  /**
   * How often the store was reset so far. The chat shell reads it to load the
   * chat on screen again when a workspace switch dropped it mid-load (KAN-1754).
   */
  resetCount: number;
  // Reset on a workspace switch
  reset: () => void;
}

/** The whole chat store: every slice reads and writes the others' fields. */
export type ChatState = OpenChatSlice &
  SessionListSlice &
  ModelsSlice &
  DraftsSlice &
  ChatResetSlice;
/** Write access to the chat store, as a slice's actions receive it. */
export type SetChatState = StoreApi<ChatState>['setState'];
/** Read access to the whole chat store, as a slice's actions receive it. */
export type GetChatState = StoreApi<ChatState>['getState'];

export const useChatStore = create<ChatState>()((set, get, store) => ({
  ...createModelsSlice(set, get, store),
  ...createDraftsSlice(set, get, store),
  ...createOpenChatSlice(set, get, store),
  ...createSessionListSlice(set, get, store),
  resetCount: 0,

  reset: () => {
    // Answers still on their way belong to the workspace being left.
    startNewWorkspace();
    forgetDraftThreads();
    set((state) => ({
      ...initialOpenChatState(),
      ...initialSessionListState(),
      ...initialDraftsState(),
      // Drop model selection so the new tenant's default kicks in via loadModels().
      ...initialModelsState(),
      resetCount: state.resetCount + 1,
    }));
  },
}));
